import { useCommentService } from '@src/hooks/useCommentService';
import { CommentStore } from '@src/stores-v2/CommentStore';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useEffect, useRef } from 'react';

import type { CommentAuthor } from '@src/stores-v2/CommentStore';

/**
 * Creates and owns a `CommentStore` for the lifetime of the open clip, mirroring
 * `useClipEditSession`. The store is the only seam the comment UI uses; it
 * receives `CommentService` and the author identity by constructor injection
 * (the store itself imports no React/hooks).
 *
 * Each aspect-ratio format is its own Video record, so the store is keyed on the
 * currently selected variant's record id. Re-keys (and refetches) when the
 * selected video changes, starts the polling host on mount, and tears everything
 * down on unmount. `undefined` (e.g. an unsaved variant) clears the view.
 */
export function useCommentStore(
  videoId: string | undefined,
  storyId?: string,
): CommentStore {
  const service = useCommentService();
  const userIdentity = useUserIdentity();
  const identity = userIdentity.currentIdentity;
  const author: CommentAuthor = {
    id: identity?.id ?? null,
    name: identity?.name ?? 'Unknown',
    email: identity?.email ?? null,
    role: identity?.role,
  };

  const storeRef = useRef<CommentStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = new CommentStore({ service, author, videoId, storyId });
  }

  // Keep author identity in sync (identity can load after the modal mounts).
  useEffect(() => {
    storeRef.current?.setAuthor(author);
  }, [author.id, author.name, author.email, author.role]);

  // Re-key (and refetch) when the selected video record changes.
  useEffect(() => {
    storeRef.current?.setClip(videoId);
  }, [videoId]);

  // Start polling on mount; stop and clear timers on unmount.
  useEffect(() => {
    const store = storeRef.current;
    store?.startPolling();
    return () => store?.dispose();
  }, []);

  return storeRef.current;
}
